import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Link,
  alpha,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import { useHistory } from "react-router-dom";

const UserLogin = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState({ email: "", password: "" });

  const handleLogin = async () => {
    const res = await hitAxios({
      path: "/api/user/login",
      post: true,
      obj: state,
    });
    if (res?.data?.success) {
      localStorage.setItem(
        process.env.REACT_APP_TOKEN + "_user",
        res.data.token,
      );
      history.push("/user");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background: isDark
          ? "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #0F172A 100%)"
          : "linear-gradient(135deg, #EEF2FF 0%, #F0F9FF 50%, #F5F3FF 100%)",
      }}
    >
      {/* Background decorations */}
      <Box
        sx={{
          position: "fixed",
          top: "10%",
          right: "15%",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: alpha(theme.palette.primary.main, 0.06),
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: "15%",
          left: "10%",
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: alpha(theme.palette.primary.main, 0.08),
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            bgcolor: isDark
              ? alpha(theme.palette.background.paper, 0.8)
              : "background.paper",
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(isDark ? "#ffffff" : "#000000", 0.08)}`,
            boxShadow: isDark
              ? `0 24px 80px ${alpha("#000", 0.5)}`
              : `0 24px 60px ${alpha("#000", 0.1)}`,
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={3.5}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                margin: "0 auto 16px",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              <CloudDownloadIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ letterSpacing: "-0.02em" }}
            >
              {lang.welcomeBack || "Welcome Back"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {lang.signInSubtitle || "Sign in to your account"}
            </Typography>
          </Box>

          <Box textAlign="right" mt={-1}>
            <Typography
              variant="caption"
              color="primary"
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => history.push("/user/forgot-password")}
            >
              Forgot password?
            </Typography>
          </Box>

          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              placeholder={lang.emailPlaceholder || "Email address"}
              type="email"
              value={state.email}
              onChange={(e) => setState({ ...state, email: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={lang.passwordPlaceholder || "Password"}
              type={showPassword ? "text" : "password"}
              value={state.password}
              onChange={(e) => setState({ ...state, password: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      size="small"
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              sx={{
                py: 1.25,
                fontWeight: 600,
                borderRadius: 2,
                mt: 0.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                "&:hover": {
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`,
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {lang.signIn || "Sign In"}
            </Button>
          </Stack>

          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            mt={2.5}
          >
            {lang.dontHaveAccount || "Don't have an account?"}{" "}
            <Link
              component="button"
              onClick={() => history.push("/user/register")}
              underline="hover"
              fontWeight={600}
              color="primary"
            >
              {lang.register || "Register"}
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default UserLogin;
